// GlobalLoading - Sprint 44 XL Branding.
// Barra de progreso indeterminada fija en la parte superior (estilo YouTube/
// GitHub). Se controla con un contador global: cualquier parte de la app
// llama begin() al iniciar un request o navegacion lenta, end() al terminar.
// Mientras el contador > 0 la barra esta visible.
//
// API:
//   import GlobalLoading, { loadingBus } from './GlobalLoading';
//   loadingBus.begin();  // ej: antes de fetch
//   try { ... } finally { loadingBus.end(); }
//
//   Hook alternativo: useGlobalLoading() -> { begin, end, busy }
//
// Integracion: en apiClient se puede envolver el fetch con begin/end.
// Sin dependencias externas. Coexiste con XLSkeleton (scope distinto).
import { useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../design/motion';

const CSS_ID = 'xl-gloading-css';
const CSS = `
@keyframes xl-gload-sweep {
  0%   { transform: translateX(-100%) scaleX(0.4) }
  50%  { transform: translateX(20%)   scaleX(0.7) }
  100% { transform: translateX(110%)  scaleX(0.4) }
}
.xl-gload {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  height: 3px;
  z-index: 1310;
  pointer-events: none;
  background: transparent;
  overflow: hidden;
}
.xl-gload__bar {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--xl-color-primary) 35%,
    var(--xl-color-secondary) 65%,
    transparent 100%
  );
  animation: xl-gload-sweep 1.1s cubic-bezier(0.4,0,0.2,1) infinite;
}
@media (prefers-reduced-motion: reduce) {
  .xl-gload__bar { animation: none; transform: scaleX(0.6) }
}
`;

// -- Event bus compartido (sin React context para poder llamarse desde
// -- modulos no-React como apiClient). Patron pub/sub minimo.
const _state = { count: 0, listeners: new Set() };

function _emit() {
  const busy = _state.count > 0;
  _state.listeners.forEach((fn) => {
    try { fn(busy); } catch (_) { /* silencio: listener individual no rompe al resto */ }
  });
}

export const loadingBus = {
  begin() {
    _state.count += 1;
    _emit();
  },
  end() {
    _state.count = Math.max(0, _state.count - 1);
    _emit();
  },
  reset() {
    _state.count = 0;
    _emit();
  },
  subscribe(fn) {
    _state.listeners.add(fn);
    try { fn(_state.count > 0); } catch (_) { /* silencio */ }
    return () => _state.listeners.delete(fn);
  },
  isBusy() { return _state.count > 0; },
};

export function useGlobalLoading() {
  const [busy, setBusy] = useState(loadingBus.isBusy());
  useEffect(() => loadingBus.subscribe(setBusy), []);
  return {
    busy,
    begin: loadingBus.begin,
    end:   loadingBus.end,
    reset: loadingBus.reset,
  };
}

export default function GlobalLoading() {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const [busy, setBusy] = useState(loadingBus.isBusy());
  useEffect(() => loadingBus.subscribe(setBusy), []);

  if (!busy) return null;

  return (
    <div className="xl-gload" role="progressbar" aria-label="Cargando" aria-busy="true">
      <span className="xl-gload__bar" />
    </div>
  );
}
